import { type CSSProperties, useState } from 'react';
import { AnimationCanvas } from './AnimationCanvas';
import { usePlayback } from './hooks/usePlayback';
import type { Pt, Shape } from './utils/cgal';
import { buildShapeFromPolygon } from './utils/cgal';
import { PRESET_SPECS } from './utils/presets';

const controlsStyle: CSSProperties = {
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
};
const canvasStyle: CSSProperties = {
  width: '100%',
  height: 460,
  border: '1px solid #d0d0d0',
  background: '#fafafa',
  display: 'block',
};
const legendItemStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: '4px' };
const swatchBase: CSSProperties = { display: 'inline-block', width: 22, height: 3 };

export const App = () => {
  const [shape, setShape] = useState<Shape | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t, setT, playing, setPlaying } = usePlayback(shape?.tMax ?? null);

  const calculateShape = (points?: Pt[]) => {
    if (!points || points.length < 3) {
      setShape(null);
      setError(null);
      return;
    }

    const result = buildShapeFromPolygon(points);
    if ('error' in result) {
      setShape(null);
      setError(result.error);
    } else {
      setShape(result);
      setError(null);
    }
  };

  const setPointsFromPreset = (key: string) => {
    setT(0);
    setPlaying(false);
    calculateShape(PRESET_SPECS[key]);
  };

  const clearPoints = () => {
    setT(0);
    setPlaying(false);
    calculateShape([]);
  };

  const togglePlay = () => setPlaying(p => !p);

  const resetPlaying = () => {
    setT(0);
    setPlaying(false);
  };

  return (
    <>
      <h1>straight skeleton — wavefront animation</h1>

      <div style={controlsStyle}>
        {Object.keys(PRESET_SPECS).map(key => (
          <button type="button" key={key} onClick={() => setPointsFromPreset(key)}>
            {key}
          </button>
        ))}
        <button type="button" style={{ justifySelf: 'end' }} onClick={clearPoints}>
          clear
        </button>
      </div>

      {error && <p style={{ color: '#cc4444' }}>{error}</p>}

      {shape ? <AnimationCanvas shape={shape} t={t} /> : <svg style={canvasStyle} />}

      <div style={{ display: 'flex', gap: '16px' }}>
        <span style={legendItemStyle}>
          <span style={{ ...swatchBase, background: '#bbb' }} />
          polygon
        </span>
        <span style={legendItemStyle}>
          <span style={{ ...swatchBase, background: '#4488cc' }} />
          wavefront
        </span>
        <span style={legendItemStyle}>
          <span style={{ ...swatchBase, background: '#cc4444' }} />
          skeleton
        </span>
        <span style={legendItemStyle}>
          <span
            style={{
              ...swatchBase,
              background: '#cc7700',
              width: 10,
              height: 10,
              borderRadius: '50%',
            }}
          />
          reflex vertex
        </span>
      </div>

      <div style={controlsStyle}>
        <button type="button" style={{ width: '100px' }} onClick={togglePlay} disabled={!shape}>
          {playing ? 'pause' : 'play'}
        </button>
        <button type="button" style={{ width: '100px' }} onClick={resetPlaying} disabled={!shape}>
          reset
        </button>
        <input
          type="range"
          min={0}
          max={1000}
          disabled={!shape}
          style={{ flex: 1, minWidth: '160px' }}
          value={shape ? (t / shape.tMax) * 1000 : 0}
          onChange={ev => {
            if (!shape) return;
            setPlaying(false);
            setT((Number(ev.target.value) / 1000) * shape.tMax);
          }}
        />
        <span>t = {t.toFixed(3)}</span>
      </div>

      {shape && <p>{shape.note}</p>}

      {shape && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa',
            padding: '4px 8px',
            border: '1px solid #d0d0d0',
          }}
        >
          <strong>events</strong>
          {shape.events.map((e, i) => {
            const fired = !e.pending && t >= e.t - 1e-9;
            return (
              <span key={i}>
                t = {e.t.toFixed(3)}: {e.label}
                {fired ? ' \u2015 fired!' : ''}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
};
